<template>
  <UiModalContainer
    footer-classes="flex justify-end gap-3"
    :modal-show="showModel"
    :modal-width="50"
    @handleClick="handleClick"
  >
    <template v-slot:header>
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 bg-gradient-to-br from-primary-blue-600 to-purple-600 rounded-xl flex items-center justify-center"
        >
          <icon class="w-6 h-6 text-white" icon="logoheader" />
        </div>
        <div>
          <h2 class="text-xl font-bold text-gray-900">PWA Guidelines</h2>
          <p class="text-sm text-gray-500">Install MyQampus on your device</p>
        </div>
      </div>
    </template>
    <template v-slot>
      <div class="py-3">
        <!-- Main content card -->
        <div
          class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100"
        >
          <div class="flex items-start gap-4">
            <!-- Content section -->
            <div class="flex-1">
              <h3 class="text-lg font-bold text-gray-900 mb-2">{{ installInfo.title }}</h3>
              <p class="text-primary-gray-600">{{ installInfo.description }}</p>
            </div>
          </div>
        </div>

        <!-- Platform-specific instructions -->
        <div class="mt-6 space-y-4">
          <div v-if="isIOS()" class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div class="flex items-center gap-3 mb-3">
              <span class="font-semibold text-gray-900">iOS Installation</span>
            </div>
            <div
              v-for="(step, index) in installInfo.steps"
              :key="index"
              class="flex items-center gap-2 text-sm text-primary-gray-600"
            >
              <div class="w-2 h-2 rounded-full bg-primary-purple-600"></div>
              <span v-if="step.includes('Share button')" class="flex items-center gap-1">
                {{ step }}
                <icon class="w-4 h-4 text-primary-blue-600" icon="ios-share" />
              </span>
              <span v-else>{{ step }}</span>
            </div>
          </div>

          <div
            v-else-if="isAndroid()"
            class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm"
          >
            <div class="flex items-center gap-3 mb-3">
              <span class="font-semibold text-gray-900">Android Installation</span>
            </div>
            <div
              v-for="(step, index) in installInfo.steps"
              :key="index"
              class="flex items-center gap-2 text-sm text-primary-gray-600"
            >
              <div class="w-2 h-2 rounded-full bg-primary-purple-600"></div>
              <span v-if="step.includes('Share button')" class="flex items-center gap-1">
                {{ step }}
                <icon class="w-4 h-4 text-primary-blue-600" icon="shareAlt" />
              </span>
              <span v-else>{{ step }}</span>
            </div>
          </div>

          <div
            v-else-if="isDesktop()"
            class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm"
          >
            <div class="flex items-center gap-3 mb-3">
              <span class="font-semibold text-gray-900">
                Desktop Installation ({{ installInfo.browser }})
              </span>
            </div>
            <div
              v-for="(step, index) in installInfo.steps"
              :key="index"
              class="flex items-center gap-2 text-sm text-primary-gray-600"
            >
              <div class="w-2 h-2 rounded-full bg-primary-purple-600"></div>
              <span v-if="step.includes('Share button')" class="flex items-center gap-1">
                {{ step }}
                <icon class="w-4 h-4 text-primary-blue-600" icon="ios-share" />
              </span>
              <span v-else>{{ step }}</span>
            </div>
          </div>
        </div>

        <!-- Benefits section -->
        <div
          class="mt-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 border border-green-100"
        >
          <h4 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            Benefits of Installing
          </h4>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div class="flex items-center gap-2 text-primary-gray-600">
              <div class="w-2 h-2 bg-primary-purple-600 rounded-full"></div>
              <span>Faster access</span>
            </div>
            <div class="flex items-center gap-2 text-primary-gray-600">
              <div class="w-2 h-2 bg-primary-purple-600 rounded-full"></div>
              <span>Offline capability</span>
            </div>
            <div class="flex items-center gap-2 text-primary-gray-600">
              <div class="w-2 h-2 bg-primary-purple-600 rounded-full"></div>
              <span>Native app experience</span>
            </div>
            <div class="flex items-center gap-2 text-primary-gray-600">
              <div class="w-2 h-2 bg-primary-purple-600 rounded-full"></div>
              <span>Push notifications</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="flex gap-3 rtl:gap-x-4">
        <UIButton button="primary" type="button" @click="skipPWAGuidelinesFor7Days">
          Skip for 7 days
        </UIButton>
      </div>
    </template>
  </UiModalContainer>
</template>

<script>
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import icon from '@components/icons/icon.vue'
import {
  isIOS,
  isAndroid,
  isDesktop,
  getInstallInstructions,
  getPlatformInfo,
} from '@utils/platformUtil.js'
import UIButton from '@src/components/UiElements/UIButton.vue'
import { getTimeInMs } from '@utils/generalUtil.js'

export default {
  name: 'PwaGuideLines',
  components: {
    UiModalContainer,
    icon,
    UIButton,
  },
  props: {
    showModel: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['handleClick'],
  data() {
    return {
      installInfo: getInstallInstructions(),
      platformInfo: getPlatformInfo(),
    }
  },
  methods: {
    handleClick() {
      this.$emit('handleClick')
    },
    isIOS() {
      return isIOS()
    },
    isAndroid() {
      return isAndroid()
    },
    isDesktop() {
      return isDesktop()
    },
    skipPWAGuidelinesFor7Days() {
      const now = Date.now()
      const sevenDays = getTimeInMs(7, 'd')
      localStorage.setItem('pwaNextCheck', (now + sevenDays).toString())
      this.handleClick()
    },
  },
}
</script>
